<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import axios from 'axios'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Search team...'
  }
})

const emit = defineEmits(['update:modelValue', 'select'])

const searchResults = ref([])
const isLoading = ref(false)
const showResults = ref(false)
let searchTimeout = null

const searchTeams = async (query) => {
  if (!query) {
    searchResults.value = []
    return
  }

  isLoading.value = true
  try {
    const { data } = await axios.get(`/api/teams/search-teams/${encodeURIComponent(query)}`)
    searchResults.value = data
  } catch (error) {
    console.error('Error searching teams:', error)
    searchResults.value = []
  } finally {
    isLoading.value = false
  }
}

const debouncedSearch = (query) => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  searchTimeout = setTimeout(() => {
    searchTeams(query)
  }, 300) // 300ms delay
}

const handleInput = (event) => {
  const value = event.target.value
  emit('update:modelValue', value)
  debouncedSearch(value)
}

const selectTeam = (team) => {
  emit('update:modelValue', team.team_name)
  emit('select', team)
  showResults.value = false
}

// Close results when clicking outside
const handleClickOutside = (event) => {
  if (!event.target.closest('.team-search')) {
    showResults.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
})
</script>

<template>
  <div class="team-search relative">
    <input 
      :value="modelValue"
      @input="handleInput"
      @focus="showResults = true"
      type="text" 
      :placeholder="placeholder"
      class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
    >
    
    <!-- Loading indicator -->
    <div v-if="isLoading" class="absolute right-3 top-1/2 transform -translate-y-1/2">
      <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-pink-500"></div>
    </div>

    <!-- Search results -->
    <div 
      v-if="showResults && searchResults.length > 0" 
      class="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
    >
      <div 
        v-for="team in searchResults" 
        :key="team.id"
        @click="selectTeam(team)"
        class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
      >
        {{ team.team_name }}
      </div>
    </div>
  </div>
</template> 